<template>
  <div
    class="ds-select-field"
    :class="{ 'ds-select-field--light': light }"
  >
    <TagIcon
      v-if="icon"
      class="ds-select-field__icon w-5 h-5"
      :icon="icon"
      color="mono-medium"
    />
    <svg
      class="ds-select-field__arrow"
      width="12"
      height="12"
      viewBox="0 0 12 12"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        fill-rule="evenodd"
        clip-rule="evenodd"
        d="M8.29325 4.29325C8.68425 3.90225 9.31625 3.90225 9.70725 4.29325C10.0982 4.68425 10.0982 5.31625 9.70725 5.70725L6.70725 8.70725C6.31625 9.09825 5.68425 9.09825 5.29325 8.70725L2.29325 5.70725C1.90225 5.31625 1.90225 4.68425 2.29325 4.29325C2.68425 3.90225 3.31625 3.90225 3.70725 4.29325L6.00025 6.58625L8.29325 4.29325Z"
        fill="#373941"
      />
    </svg>
    <select
      :id="id"
      class="ds-select-field__select"
      :name="name"
      :class="classes"
      :readonly="readonly"
      :disabled="isDisabled"
      :value="modelValue"
      :aria-invalid="isMarkedAsInvalid || (hasErrors && !val)"
      :aria-label="ariaLabel"
      :data-test-id="dataTestId"
      @input="input($event.target.value)"
      @click="onSelectClick"
      @focus="onSelectFocus"
    >
      <option
        v-if="placeholder"
        value=""
        :disabled="!isOptional"
        selected
      >
        {{ placeholder }}
      </option>
      <slot v-if="options.length === 0" />
      <template v-else-if="hasOptionGroups">
        <optgroup
          v-for="(options, optgroup) in optionsByOptgroup"
          :label="optgroup"
          :key="optgroup"
        >
          <option
            v-for="option in options"
            :key="option.value"
            :value="option.value"
          >
            {{ option.label }}
          </option>
        </optgroup>
      </template>
      <template v-else>
        <option
          v-for="option in options"
          :key="option.value"
          :value="option.value"
          :class="{ 'hidden': option.hidden }"
        >
          {{ option.label }}
        </option>
      </template>
    </select>
    <FormCaption
      v-if="caption"
      type="info"
      class="mt-1"
    >
      {{ caption }}
    </FormCaption>
    <FormCaption
      v-if="hasErrors && !val"
      type="invalid"
      class="mt-1"
    >
      {{ firstError }}
    </FormCaption>
  </div>
</template>

<script>
import TagIcon from '@/components/TagIcon.vue';

import htmlProps from '../htmlProps.js';
import commonProps from './common-props-mixin';
import FormCaption from './FormCaption.vue';
import validation from './validation-mixin';

export default {
  name: 'BaseSelectField',
  // keep input to prevent emitting native event when v-bind=$props is used
  emits: ['change', 'update:modelValue', 'input', 'selectClick', 'selectOpen'],
  mixins: [validation, commonProps, htmlProps],
  components: {
    TagIcon,
    FormCaption,
  },
  props: {
    modelValue: {
      type: [String, Number],
    },
    options: {
      type: [Array, Object],
      default() {
        return [];
      },
    },
    id: {
      type: String,
      default: null,
    },
    placeholderValue: {
      type: [String, Number],
      default: null,
    },
    isOptional: {
      type: Boolean,
      required: false,
      default: false,
    },
    light: {
      type: Boolean,
      default: false,
    },
    ariaLabel: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      val: this.modelValue,
    };
  },
  computed: {
    classes() {
      if (this.placeholder && !this.val && this.val?.toString() !== '0') {
        return 'ds-select-field__select--placeholder';
      }
      if (this.placeholderValue && this.val === this.placeholderValue) {
        return 'ds-select-field__select--placeholder';
      }
      return null;
    },
    hasOptionGroups() {
      return this.options.some((option) => option.optgroup);
    },
    optionsByOptgroup() {
      return this.options.reduce((acc, option) => {
        if (option.optgroup) {
          if (!acc[option.optgroup]) {
            acc[option.optgroup] = [];
          }
          acc[option.optgroup].push(option);
        }
        return acc;
      }, {});
    },
  },
  watch: {
    modelValue(value) {
      this.val = value;
    },
  },
  methods: {
    onSelectClick(e) {
      if (e.offsetY > 0) {
        this.$emit('selectClick');
      }
    },
    onSelectFocus() {
      this.$emit('selectOpen');
    },
    input(val) {
      this.val = val;
      this.$emit('update:modelValue', val); // Don't change the order
      this.$emit('change', val);
    },
  },
};
</script>
